// libraries
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store';

interface OrchestratorPricingCommitteeState {
  nid: string;
  selectedStep: string;
  selectedResponse: number;
  selectedStatus: string;
  selectedProcessStatus: string;
  selectedProcess: string;
  selectedAlias: string;
  sortByOldest: boolean;
  start_date: string;
  end_date: string;
  editorForEventOrchestrator: {
    status: 'COMPLETED' | 'IN_PROGRESS' | 'REJECTED';
    comment: string;
  };
  editorPayload: Record<string, unknown>;
  editorPayloadTask: Record<string, unknown>;
  editorTaskType: string;
}

export const initialState: OrchestratorPricingCommitteeState = {
  nid: '',
  selectedStep: '',
  selectedResponse: -1,
  selectedStatus: 'IN_PROGRESS',
  selectedProcessStatus: '',
  selectedProcess: '',
  selectedAlias: 'market-maker',
  sortByOldest: false,
  start_date: '',
  end_date: '',
  editorForEventOrchestrator: { status: 'IN_PROGRESS', comment: '' },
  editorPayload: {},
  editorPayloadTask: {},
  editorTaskType: '',
};

export const domainOrchestratorPricingCommitteeSlice = createSlice({
  name: 'domainOrchestratorPricingCommittee',
  initialState,
  reducers: {
    setNid: (state, action: PayloadAction<string>) => {
      state.nid = action.payload;
      state.selectedStep = '';
    },
    setSelectedStep: (state, action: PayloadAction<string>) => {
      state.selectedStep = action.payload;
    },
    setSelectedResponse: (state, action: PayloadAction<number>) => {
      state.selectedResponse = action.payload;
    },
    setSelectedStatus: (state, action: PayloadAction<string>) => {
      state.selectedStatus = action.payload;
    },
    setSelectedProcessStatus: (state, action: PayloadAction<string>) => {
      state.selectedProcessStatus = action.payload;
    },
    setSelectedProcess: (state, action: PayloadAction<string>) => {
      state.selectedProcess = action.payload;
    },
    setSelectedAlias: (state, action: PayloadAction<string>) => {
      state.selectedAlias = action.payload;
    },
    setStartDate: (state, action: PayloadAction<string>) => {
      state.start_date = action.payload;
    },
    setEndDate: (state, action: PayloadAction<string>) => {
      state.end_date = action.payload;
    },
    setEditorStatus: (
      state,
      action: PayloadAction<'COMPLETED' | 'IN_PROGRESS' | 'REJECTED'>
    ) => {
      state.editorForEventOrchestrator.status = action.payload;
    },
    setEditorComment: (state, action: PayloadAction<string>) => {
      state.editorForEventOrchestrator.comment = action.payload;
    },
    setEditorPayload: (
      state,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      state.editorPayload = action.payload;
    },
    setEditorPayloadTask: (
      state,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      state.editorPayloadTask = action.payload;
    },
    clearEditorPayload: state => {
      state.editorPayload = {};
    },
    clearEditorPayloadTask: state => {
      state.editorPayloadTask = {};
    },
    setEditorTaskType: (state, action: PayloadAction<string>) => {
      state.editorTaskType = action.payload;
    },
    clearEditorTaskType: state => {
      state.editorTaskType = '';
    },
    toggleSortByOldest: state => {
      state.sortByOldest = !state.sortByOldest;
    },
    clearNid: state => {
      state.nid = '';
    },
    clearSelectedStep: state => {
      state.selectedStep = '';
    },
    clearSelectedResponse: state => {
      state.selectedResponse = -1;
    },
    clearSelectedStatus: state => {
      state.selectedStatus = 'IN_PROGRESS';
    },
    clearSelectedProcessStatus: state => {
      state.selectedProcessStatus = '';
    },
    clearSelectedProcess: state => {
      state.selectedProcess = '';
    },
    clearSelectedAlias: state => {
      state.selectedAlias = 'market-maker';
    },
    clearEditorStatus: state => {
      state.editorForEventOrchestrator.status = 'IN_PROGRESS';
    },
    clearEditorComment: state => {
      state.editorForEventOrchestrator.comment = '';
    },
  },
});

export const {
  setNid,
  clearNid,
  setSelectedStep,
  clearSelectedStep,
  setSelectedResponse,
  clearSelectedResponse,
  setSelectedStatus,
  clearSelectedStatus,
  setSelectedProcessStatus,
  clearSelectedProcessStatus,
  setSelectedProcess,
  setSelectedAlias,
  clearSelectedProcess,
  clearSelectedAlias,
  setStartDate,
  setEndDate,
  setEditorStatus,
  setEditorComment,
  setEditorPayload,
  toggleSortByOldest,
  clearEditorStatus,
  clearEditorComment,
  clearEditorPayload,
  setEditorPayloadTask,
  clearEditorPayloadTask,
  setEditorTaskType,
  clearEditorTaskType,
} = domainOrchestratorPricingCommitteeSlice.actions;

export default domainOrchestratorPricingCommitteeSlice.reducer;

export const getOrchestratorNid = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.nid;

export const getSelectedStep = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedStep;

export const getSelectedResponse = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedResponse;

export const getSelectedStatus = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedStatus;

export const getSelectedProcessStatus = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedProcessStatus;

export const getSelectedProcess = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedProcess;

export const getSelectedAlias = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.selectedAlias;

export const getSortByOldest = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.sortByOldest;

export const getStartDate = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.start_date;

export const getEndDate = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.end_date;

export const getEditorStatus = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.editorForEventOrchestrator.status;

export const getEditorComment = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.editorForEventOrchestrator.comment;

export const getEditorPayload = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.editorPayload;

export const getEditorPayloadTask = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.editorPayloadTask;

export const getEditorTaskType = (state: RootState) =>
  state.domainOrchestratorPricingCommittee.editorTaskType;
